import ExportIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Export.js';
import { PageHeader } from '@diligentcorp/atlas-theme-mui-lens';
import { PageHeader as ModernPageHeader } from '@diligentcorp/atlas-theme-mui/lib/themes/lens/components.js';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { CASES_CSV } from 'src/api';
import axios from 'src/axios';
import { CasesAuditLogDrawer } from 'src/components/atlas/organisms/CasesAuditLogDrawer';
import { CaseDataDownloadButton } from 'src/components/molecules';
import { CaseCreateNewButton } from 'src/components/molecules/CaseCreateNewButton/CaseCreateNewButton';
import { handleErrorToast } from 'src/components/molecules/Toast';
import { downloadFile } from 'src/utils/commonFunctions';
import { DateFormats, formatDate, getNow } from 'src/utils/time';

interface SpeakUpCasesHeaderProps {
  modernizationEnabled: boolean;
  caseDataDownloadEnabled: boolean | undefined;
  addNewReport: () => void;
  addExistingReport: () => void;
}

export const SpeakUpCasesHeader: React.FC<SpeakUpCasesHeaderProps> = ({
  modernizationEnabled,
  caseDataDownloadEnabled,
  addNewReport,
  addExistingReport,
}) => {
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  const handleExport = async () => {
    const currentTimestamp = getNow({ returnAsDate: true });
    const dateFormatted = formatDate(
      currentTimestamp,
      DateFormats.YYYY_MM_DD_HH_MM,
    );
    const filename = `CaseDownload-${dateFormatted}.csv`;

    setExportLoading(true);
    try {
      const response = await axios.get(CASES_CSV, { timeout: 60000 });
      downloadFile(new Blob([response.data]), filename);
      setExportLoading(false);
      setExportModalOpen(false);
    } catch {
      setExportLoading(false);
      handleErrorToast({
        toastMessage: 'Oops! Something went wrong',
        toastDescription:
          "Sorry, we can't download the data now. Please check your connection and try again later.",
      });
    }
  };

  if (!modernizationEnabled) {
    return (
      <div>
        <PageHeader
          pageTitle="Speak Up"
          pageSubtitle="Access and manage speak up cases"
          moreButton={
            <Box sx={{ display: 'flex', gap: 2 }}>
              {caseDataDownloadEnabled && <CaseDataDownloadButton />}
              <CaseCreateNewButton
                addNewReport={addNewReport}
                addExistingReport={addExistingReport}
              />
            </Box>
          }
        />
      </div>
    );
  }

  return (
    <>
      <Dialog
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Download all case data</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You are about to download sensitive data outside of Vault Platform.
            As authorized personnel, you are entrusted with protecting your
            organisations&apos; data and all persons concerned. Remember to
            follow internal policies and restrict access solely to authorized
            personnel.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setExportModalOpen(false)}
            disabled={exportLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={exportLoading}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <CasesAuditLogDrawer
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <ModernPageHeader
          pageTitle="Speak up cases"
          pageSubtitle="Manage and review submits to all speak up forms"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {caseDataDownloadEnabled && (
            <Button
              variant="text"
              color="primary"
              size="medium"
              data-testid="audit-log-button"
              sx={{ whiteSpace: 'nowrap' }}
              onClick={() => setAuditLogOpen(true)}
            >
              Audit log
            </Button>
          )}
          {caseDataDownloadEnabled && (
            <Button
              variant="outlined"
              size="medium"
              data-testid="export-button"
              startIcon={<ExportIcon aria-hidden />}
              onClick={() => setExportModalOpen(true)}
            >
              Export
            </Button>
          )}
          <CaseCreateNewButton
            addNewReport={addNewReport}
            addExistingReport={addExistingReport}
          />
        </Box>
      </Box>
    </>
  );
};
